import {Observable, Unsubscribable} from "rxjs";
import {EventBase, SurveyModel} from "survey-core";

/**
 * Helper class to hold a series of {@link Unsubscribable}'s that can be easily added to and unsubscribed in bulk
 */
export class SubscriptionHandler {
	/** Subscriptions added */
	private subscriptions:Unsubscribable[] = [];

	/** Add a standard rxjs {@link Observable} or SurveyJS {@link EventBase} subscription to the tracked subscriptions */
	addSubscription<T>(observable:Observable<T>, func:SubscriptionFunction<T>):Unsubscribable;
	addSubscription<T>(event:EventBase<SurveyModel, T>, func:EventFunction<T>):Unsubscribable;
	addSubscription<T>(obj:Observable<T> | EventBase<SurveyModel, T>, func:SubscriptionFunction<T> | EventFunction<T>):Unsubscribable {
		if(obj instanceof EventBase) {
			const unsubscribable = SubscriptionHandler.getEventSubscription(obj, <EventFunction<T>>func)
			this.subscriptions.push(unsubscribable);
			return unsubscribable;
		}

		const subscription = obj.subscribe(<SubscriptionFunction<T>>func)
		this.subscriptions.push(subscription);
		return subscription;
	}

	/** Unsubscribe to all subscriptions, rxjs or SurveyJS */
	unsubscribeAll() {
		for(const subscription of this.subscriptions) {
			subscription.unsubscribe();
		}

		this.subscriptions = [];
	}

	/**
	 * Subscribe to a SurveyJS {@link EventBase} and return an {@link Unsubscribable} for unsubscribing from it later
	 * @param event the event to subscribe to
	 * @param func the function to call when the event is triggered
	 */
	static getEventSubscription<T>(event:EventBase<SurveyModel, T>, func:EventFunction<T>):Unsubscribable {
		event.add(<EventFunction<T>>func);

		return {
			unsubscribe():void {
				event.remove(<EventFunction<T>>func);
			}
		}
	}
}

type SubscriptionFunction<T> = (value:T) => void;
type EventFunction<T> = (sender:SurveyModel, options:T) => void;
